//@flow
import EntityActions from 'common/redux/actions/EntityActions';

import { LocusApiModel } from 'common/modules/api';
import type ImmutableModel from 'common/models/flow/ImmutableModel';
import { bulkLocationFetch as locationSearchApi } from 'common/middleware/api';
import ControlledSetInterval from 'common/utils/ControlledSetInterval';

export default class PaginatedEntityActions<
    TEntity: ImmutableModel,
    TParams
> extends EntityActions<TEntity, TParams> {
    bulkFetchApi: LocusApiModel;
    geocodeApi: LocusApiModel;

    constructor(
        TAG: string,
        api: LocusApiModel,
        bulkFetchApi: LocusApiModel,
        disableApi: LocusApiModel,
        bulkDisableApi: LocusApiModel,
        validateEntityIdsApi: LocusApiModel,
        geocodeApi: ?LocusApiModel,
        entityStatusUpdateApi: ?LocusApiModel,
        resetPasswordApi: ?LocusApiModel,
        bulkResetPasswordApi: ?LocusApiModel,
        bulkResetQrCodeApi: ?LocusApiModel,
        bulkUpdateEntitiesApi: ?LocusApiModel
    ) {
        super(
            TAG,
            api,
            disableApi,
            bulkDisableApi,
            validateEntityIdsApi,
            entityStatusUpdateApi,
            resetPasswordApi,
            bulkResetPasswordApi,
            bulkResetQrCodeApi,
            bulkUpdateEntitiesApi
        );
        this.bulkFetchApi = bulkFetchApi;
        this.geocodeApi = geocodeApi;
    }

    fetchEntitiesCall = (
        params: TParams,
        body: Object,
        options: FetchEntitiesOptionsType = {}
    ) => {
        const { processResponse, onSuccess } = options;
        return this.bulkFetchApi.post(params, body).then((response: any) => {
            let entities = null;
            if (processResponse) {
                entities = processResponse(response);
            } else {
                entities = (response: Array<Object>);
            }
            entities = { ...entities };
            onSuccess &&
                onSuccess(entities, {
                    params,
                    body
                });
            return { entities, response };
        });
    };

    fetchEntities = (
        params: TParams,
        body: Object,
        options: FetchEntitiesOptionsType = {}
    ) => {
        const { isPolling } = options;
        return (dispatch: Dispatch<*>) => {
            !options.dontShowLoader &&
                dispatch(
                    this.emitFetchEntitiesStart(params, body, { isPolling })
                );
            return this.fetchEntitiesCall(params, body, options)
                .then(
                    ({
                        entities,
                        response
                    }: {
                        entities: Array<Object>,
                        response: any
                    }) => {
                        !options.dontInjectSuccess &&
                            dispatch(
                                this.injectEntities(params, entities, body, {
                                    isPolling
                                })
                            );
                        return response;
                    }
                )
                .catch((error: Error) => {
                    dispatch(
                        this.emitFetchEntitiesFailure(params, error, body, {
                            isPolling
                        })
                    );
                    this.handleError(error, options, dispatch);
                });
        };
    };

    pollFetchEntities = (
        params: Object,
        body: Object,
        options: Object = {}
    ) => {
        const poller = new ControlledSetInterval();
        const { onSuccessCallback, onFailureCallback } = options;
        let filters = [
            { name: 'status', operation: 'EQUALS', value: 'ACTIVE' }
        ];
        if (body.filters) {
            filters = [...filters, ...body.filters];
        }
        const fetchEntities = () =>
            this.fetchEntities(
                params,
                { ...body, filters },
                { isPolling: true }
            );
        return (dispatch: Dispatch<*>) => {
            return poller.initiateStatelessPolling({
                apiModel: this.bulkFetchApi,
                apiMethod: 'POST',
                params,
                callback: () => dispatch(fetchEntities()),
                onSuccessCallback,
                onFailureCallback
            });
        };
    };

    pollLocationSearch = (
        params: TParams,
        body: Object,
        options: Object = {}
    ) => {
        const poller = new ControlledSetInterval();
        const { onSuccess, onFailure } = options;
        const onSuccessCallback = (response: any) => {
            const entities = (response: Array<Object>);
            const { locations } = entities;
            onSuccess && onSuccess(locations);
        };
        return (dispatch: Dispatch<*>) => {
            return poller.initiateStatelessPolling({
                apiModel: this.bulkFetchApi,
                apiMethod: 'POST',
                params,
                callback: () => dispatch(this.locationSearch(params, body)),
                onSuccessCallback,
                onFailureCallback: onFailure
            });
        };
    };
    locationSearch = (params: TParams, body: Object) => {
        return () => {
            return locationSearchApi
                .post(params, body)
                .then(response => response);
        };
    };
}
